import { fetchMakes, fetchModels, fetchYears, fetchEngineTypes } from 'api/modules/vehicles'
import { spacing } from 'constants/global'
import { FormattedMessage, IntlShape, useIntl } from 'locales/index'
import moment from 'moment-timezone'
import React from 'react'
import {
  Alert,
  Button,
  Col,
  FetchSelect,
  Form,
  FormInstance,
  FormItemProps,
  GenericError,
  Input,
  LabeledValue,
  Row,
  SelectableCardGrid,
  Spin,
  Typography,
  RadioButtonCard,
  DefaultOptionType
} from '@commutifi-fe/ui'
import { useAsync } from 'shared/hooks/useAsync'
import useVehicle, { UseVehicleContextValue, UseVehicleFields } from 'shared/hooks/useVehicle'
import { FormItemInjectedProps } from 'shared/interfaces'
import styles from './styles.module.scss'

const { Link, Paragraph, Text } = Typography
export interface IVehicleValue {
  modelId?: LabeledValue['value']
  engineType?: React.ReactNode
  model?: React.ReactNode
  make?: string
  year?: number
  genericModelId?: string
}

export interface IVehiclePickerProps extends FormItemInjectedProps<IVehicleValue> {
  initialVehicle?: IVehicleValue
  form?: FormInstance
  children: React.ReactNode
  /**
   * Whether to allow the user to enter a custom vehicle using free text inputs
   * @defaultValue true
   */
  allowCustom?: boolean
  /**
   * If false, the component will not trigger any external processing. (No years, makes or models fetching will be triggered)
   * This is used to render the component as a display only.
   * @example Set to false in survey builder to show the component but allow 0 interactions with it
   */
  isInteractive?: boolean
}

interface FormItemWithInputProps extends FormItemProps {
  placeholder?: React.ReactNode
}

export interface IVehicleObject {
  uuid: string
  name: string
}

// Thanks to Kent C. Dodds for the inspiration:
// https://kentcdodds.com/blog/compound-components-with-react-hooks
const VehiclePickerContext = React.createContext<
  | (Omit<UseVehicleContextValue & { intl: IntlShape; allowCustom: boolean }, 'setIsUnknown' | 'resetVehicle'> & {
      toggleIsUnknown: React.MouseEventHandler
    })
  | null
>(null)

export const validateVehicle = (rule: any, vehicle: IVehicleValue) => {
  if (!vehicle || (!vehicle.modelId && !vehicle.genericModelId)) {
    // eslint-disable-next-line prefer-promise-reject-errors -- We need to return a string
    return Promise.reject(<FormattedMessage id="components.vehicleSelector.errors.required" />)
  }
  return Promise.resolve()
}
const search = (input: string, { props: { key } }: any) => {
  // Escape \ characters in user input. See https://stackoverflow.com/questions/31814535/getting-error-invalid-regular-expression
  const parsedInput = input.replace(/\\/g, '\\\\')
  // Check if the value (option) starts with the input (search). It's also case insensitive
  return `${key}`.search(new RegExp(parsedInput, 'i')) === 0
}

// The common props used for the select components
const selectCommonProps = {
  filterOption: search
}

export function VehiclePicker({
  id,
  onChange,
  initialVehicle,
  form,
  children,
  allowCustom = true,
  isInteractive = true
}: IVehiclePickerProps) {
  const intl = useIntl()

  const {
    selectYear,
    selectMake,
    selectModel,
    selectEngineType,
    setIsUnknown,
    setIsCustom,
    registerFieldName,
    resetVehicle,
    year,
    make,
    model,
    engineType,
    isUnknown,
    isCustom
  }: UseVehicleContextValue = useVehicle({
    initialYear: initialVehicle?.year,
    initialMake: initialVehicle?.make,
    initialModel:
      initialVehicle?.modelId && initialVehicle?.model
        ? { key: initialVehicle.modelId.toString(), value: initialVehicle.modelId, label: initialVehicle.model }
        : undefined,
    initialEngineType:
      initialVehicle?.modelId && initialVehicle?.engineType
        ? { key: initialVehicle.modelId.toString(), value: initialVehicle.modelId, label: initialVehicle.engineType }
        : undefined,
    initialIsUnknown: Boolean(initialVehicle?.genericModelId),
    form
  })

  const {
    data: genericModels,
    loading: isFetchingGenericModels,
    error: fetchGenericsError,
    retry: triggerFetchGenericModels
  } = useAsync<IVehicleObject[]>(() => fetchModels({ generic: true }), [], {
    triggerOnMount: isUnknown && !isInteractive
  })

  const handleOnChange = React.useCallback((value: IVehicleValue | null) => onChange && onChange(value), [onChange])
  const handleSelectModel = React.useCallback(
    (vehicleModel: LabeledValue) => {
      selectModel(vehicleModel)
      handleOnChange(
        vehicleModel
          ? {
              modelId: vehicleModel.value,
              model: vehicleModel.label,
              engineType: undefined,
              make,
              year
            }
          : null
      )
    },
    [selectModel, year, make, handleOnChange]
  )

  const handleSelectEngineType = React.useCallback(
    (vehicleEngineType: LabeledValue) => {
      selectEngineType(vehicleEngineType)
      handleOnChange(
        vehicleEngineType
          ? {
              modelId: vehicleEngineType.value,
              engineType: vehicleEngineType.label,
              model: model ? model.label : undefined,
              make,
              year
            }
          : null
      )
    },
    [selectEngineType, year, make, model, handleOnChange]
  )

  const handleSelectGeneric = React.useCallback(
    (genericModelId: string) => {
      resetVehicle()
      handleOnChange({ genericModelId })
    },
    [resetVehicle, handleOnChange]
  )

  const toggleIsUnknown: React.MouseEventHandler = React.useCallback(
    (e) => {
      e.preventDefault()
      const toggledIsUnknown = !isUnknown
      setIsUnknown(toggledIsUnknown)
      if (toggledIsUnknown) {
        !genericModels && triggerFetchGenericModels()
      } else {
        handleOnChange({ ...initialVehicle, genericModelId: undefined })
      }
    },
    [isUnknown, setIsUnknown, genericModels, triggerFetchGenericModels, handleOnChange, initialVehicle]
  )

  const value = React.useMemo(
    () => ({
      selectYear,
      selectMake,
      selectModel: handleSelectModel,
      selectEngineType: handleSelectEngineType,
      registerFieldName,
      year,
      make,
      model,
      engineType,
      isUnknown,
      toggleIsUnknown,
      isCustom,
      setIsCustom,
      intl,
      allowCustom: Boolean(allowCustom),
      isInteractive
    }),
    [
      selectYear,
      selectMake,
      handleSelectModel,
      handleSelectEngineType,
      registerFieldName,
      year,
      make,
      model,
      engineType,
      isUnknown,
      toggleIsUnknown,
      isCustom,
      setIsCustom,
      intl,
      allowCustom,
      isInteractive
    ]
  )

  return (
    <VehiclePickerContext.Provider value={value}>
      {isCustom ? (
        <Alert
          type="info"
          style={{ marginBottom: spacing.medium2 }}
          showIcon
          message={intl.formatMessage({ id: 'global.vehicle.notList.info.message' })}
          description={
            <Paragraph>
              {intl.formatMessage({ id: 'global.vehicle.notList.info.text' })}
              &nbsp;
              {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
              <Link onClick={() => setIsCustom(false)}>
                {intl.formatMessage({ id: 'global.vehicle.notList.info.showVehiclePickers' })}
              </Link>
            </Paragraph>
          }
        />
      ) : null}
      <span id={id} className={styles['vehicle-picker-container']}>
        {children}
        {isUnknown ? (
          <Row>
            <Col span={24}>
              {fetchGenericsError ? (
                <GenericError
                  className="u-margin-top--small"
                  hideErrorIcon
                  description={<FormattedMessage id="components.vehicleSelector.errors.fetching.generic" />}
                  actions={
                    <Button onClick={triggerFetchGenericModels} loading={isFetchingGenericModels}>
                      <span>
                        <FormattedMessage id="global.retry" />
                      </span>
                    </Button>
                  }
                />
              ) : (
                <Spin spinning={Boolean(isFetchingGenericModels)}>
                  <div className={styles['dontknow-choice-container']}>
                    <div className="u-flex-column u-flex-justify-center u-flex-align-center">
                      {!isFetchingGenericModels && genericModels ? (
                        <>
                          <div className="u-margin-bottom--medium u-font-16 u-font-weight-semi">
                            <FormattedMessage id="components.vehicleSelector.generics.title" />
                          </div>
                          <SelectableCardGrid
                            equalWidth
                            onChange={handleSelectGeneric}
                            multiSelect={false}
                            initialSelectedCards={initialVehicle?.genericModelId ? [initialVehicle.genericModelId] : []}
                          >
                            {genericModels.map((m: IVehicleObject) => (
                              <SelectableCardGrid.ClickableCard id={m.uuid} key={m.uuid} title={m.name} />
                            ))}
                          </SelectableCardGrid>
                        </>
                      ) : null}
                    </div>
                  </div>
                </Spin>
              )}
            </Col>
          </Row>
        ) : null}
      </span>
    </VehiclePickerContext.Provider>
  )
}

const useVehiclePickerContext = () => {
  const context = React.useContext(VehiclePickerContext)
  if (!context) {
    throw new Error('compound components cannot be rendered outside the VehiclePicker component')
  }
  return context
}

function VehicleYear({ placeholder, name, rules, ...formItemProps }: FormItemWithInputProps) {
  const { intl, year, isUnknown, selectYear, registerFieldName, isCustom, allowCustom, isInteractive } =
    useVehiclePickerContext()
  React.useEffect(() => {
    if (name) {
      registerFieldName(UseVehicleFields.Year, name)
    }
  }, [name, registerFieldName])

  const renderYears = React.useCallback(
    (years: number[] | undefined) =>
      // Important key is used in the search so keep as is
      (years || []).map((year) => ({ key: year, value: year, label: year })),
    []
  )

  return (
    <Form.Item
      name={name}
      {...formItemProps}
      validateTrigger="onBlur"
      rules={[
        ...(rules || []),
        {
          validator: (_, value) => {
            const maxYear = moment().year() + 1
            if (value < 1900 || value > maxYear) {
              return Promise.reject(
                new Error(intl.formatMessage({ id: 'validation.vehicle.years.minMax' }, { maxYear }))
              )
            }
            return Promise.resolve()
          }
        }
      ]}
    >
      {isCustom && allowCustom ? (
        // eslint-disable-next-line jsx-a11y/no-autofocus -- We might want to check the impact on accessibility
        <Input autoFocus={!year} placeholder={intl.formatMessage({ id: 'global.vehicle.select.year.placeholder' })} />
      ) : (
        <FetchSelect
          data-testid={VehiclePicker.Year.id}
          {...selectCommonProps}
          placeholder={placeholder || <FormattedMessage id="label.year" />}
          value={year}
          onChange={selectYear}
          disabled={isUnknown}
          query={fetchYears}
          queryKey="vehicles/years"
          queryOptions={{ enabled: isInteractive }}
          renderOptions={renderYears}
          aria-label="Vehicle year"
        />
      )}
    </Form.Item>
  )
}

function VehicleMake({ placeholder, name, ...formItemProps }: FormItemWithInputProps) {
  const { intl, year, make, isUnknown, isCustom, allowCustom, selectMake, registerFieldName, isInteractive } =
    useVehiclePickerContext()
  React.useEffect(() => {
    if (name) {
      registerFieldName(UseVehicleFields.Make, name)
    }
  }, [name, registerFieldName])

  const renderMakes = React.useCallback(
    (makes: string[] | undefined) =>
      (allowCustom
        ? [
            {
              key: useVehicle.CUSTOM_VEHICLE_VALUE_STR,
              value: useVehicle.CUSTOM_VEHICLE_VALUE_STR,
              label: intl.formatMessage({ id: 'label.select.car.notListed' })
            }
          ]
        : []
      ).concat(
        (makes || []).map((make: string) => ({
          // Important key is used in the search so keep as is
          key: make,
          value: make,
          label: make
        }))
      ),
    [allowCustom, intl]
  )

  return (
    <Form.Item name={name} {...formItemProps}>
      {isCustom && allowCustom ? (
        <Input
          // eslint-disable-next-line jsx-a11y/no-autofocus -- We might want to check the impact on accessibility
          autoFocus={Boolean(year && !make)}
          placeholder={intl.formatMessage({ id: 'global.vehicle.select.make.placeholder' })}
        />
      ) : (
        <FetchSelect<string[]>
          data-testid={VehiclePicker.Make.id}
          {...selectCommonProps}
          placeholder={placeholder || <FormattedMessage id="label.make" />}
          value={make}
          onChange={selectMake}
          disabled={isUnknown || !year}
          query={fetchMakes}
          queryOptions={{ enabled: Boolean(year && isInteractive) }}
          queryKey="vehicles/makes"
          queryParams={[{ year }]}
          renderOptions={renderMakes}
          aria-label="Vehicle make"
        />
      )}
    </Form.Item>
  )
}

function VehicleModel({ placeholder, name, ...formItemProps }: FormItemWithInputProps) {
  const { intl, year, make, model, isUnknown, isCustom, allowCustom, selectModel, registerFieldName, isInteractive } =
    useVehiclePickerContext()
  React.useEffect(() => {
    if (name) {
      registerFieldName(UseVehicleFields.Model, name)
    }
  }, [name, registerFieldName])

  const renderModels = React.useCallback(
    (models: IVehicleObject[] | undefined) =>
      (allowCustom
        ? [
            {
              key: useVehicle.CUSTOM_VEHICLE_VALUE_STR,
              value: useVehicle.CUSTOM_VEHICLE_VALUE_STR,
              label: intl.formatMessage({ id: 'label.select.car.notListed' })
            }
          ]
        : []
      ).concat(
        (models || []).map((model: IVehicleObject) => ({
          // Important key is used in the search so keep as is
          key: model.uuid,
          value: model.uuid,
          label: model.name
        }))
      ),
    [allowCustom, intl]
  )

  return (
    <Form.Item name={name} {...formItemProps}>
      {isCustom && allowCustom ? (
        <Input
          // eslint-disable-next-line jsx-a11y/no-autofocus -- We might want to check the impact on accessibility
          autoFocus={Boolean(make && !model)}
          placeholder={intl.formatMessage({ id: 'global.vehicle.select.model.placeholder' })}
        />
      ) : (
        <FetchSelect<IVehicleObject[], IVehicleObject[], LabeledValue>
          data-testid={VehiclePicker.Model.id}
          {...selectCommonProps}
          placeholder={placeholder || <FormattedMessage id="label.model" />}
          value={model}
          labelInValue
          onChange={selectModel}
          disabled={isUnknown || !year || !make}
          query={fetchModels}
          queryOptions={{ enabled: Boolean(year && make && isInteractive) }}
          queryKey="vehicles/models"
          queryParams={[{ year, make }]}
          renderOptions={renderModels}
          aria-label="Vehicle model"
        />
      )}
    </Form.Item>
  )
}
function VehicleFuelType({ placeholder, name, ...formItemProps }: FormItemWithInputProps) {
  const {
    intl,
    year,
    make,
    model,
    engineType,
    isUnknown,
    isCustom,
    allowCustom,
    selectEngineType,
    registerFieldName,
    isInteractive
  } = useVehiclePickerContext()
  React.useEffect(() => {
    if (name) {
      registerFieldName(UseVehicleFields.FuelType, name)
    }
  }, [name, registerFieldName])

  const renderFuelTypes = React.useCallback(
    (engineTypes: IVehicleObject[] | undefined): DefaultOptionType[] =>
      (allowCustom
        ? ([
            {
              key: useVehicle.CUSTOM_VEHICLE_VALUE_STR,
              value: useVehicle.CUSTOM_VEHICLE_VALUE_STR,
              label: intl.formatMessage({ id: 'label.select.car.notListed' })
            }
          ] as DefaultOptionType)
        : []
      ).concat(
        (engineTypes || []).map((engineType: IVehicleObject) => ({
          // Important key is used in the search so keep as is
          key: engineType.uuid,
          value: engineType.uuid,
          label: <Text capitalize>{engineType.name}</Text>
        }))
      ),
    [allowCustom, intl]
  )

  const fetchEngineTypesCallback = React.useCallback(
    async (...args: Parameters<typeof fetchEngineTypes>) => {
      const engines = await fetchEngineTypes(...args)
      if (engines && engines.length === 1) {
        selectEngineType({
          key: engines[0].name,
          value: engines[0].uuid,
          label: engines[0].name
        })
      }
      return engines
    },
    [selectEngineType]
  )

  return (
    <Form.Item name={name} {...formItemProps}>
      {isCustom && allowCustom ? (
        <Input
          // eslint-disable-next-line jsx-a11y/no-autofocus -- We might want to check the impact on accessibility
          autoFocus={Boolean(make && model && !engineType)}
          placeholder={intl.formatMessage({ id: 'global.vehicle.select.engineType.placeholder' })}
        />
      ) : (
        <FetchSelect<IVehicleObject[], IVehicleObject[], LabeledValue>
          data-testid={VehiclePicker.EngineType.id}
          {...selectCommonProps}
          placeholder={placeholder || <FormattedMessage id="label.fuelType" />}
          value={engineType}
          style={{ textTransform: 'capitalize' }}
          labelInValue
          onChange={selectEngineType}
          disabled={isUnknown || !year || !make || !model}
          query={fetchEngineTypesCallback}
          queryOptions={{ enabled: Boolean(year && make && model && isInteractive) }}
          queryKey="vehicles/engineType"
          queryParams={[{ year, make, model: model?.label }]}
          renderOptions={renderFuelTypes}
          aria-label="Vehicle fuel type"
        />
      )}
    </Form.Item>
  )
}

function GenericVehicleOption({ name, ...formItemProps }: FormItemProps) {
  const { isUnknown, toggleIsUnknown, registerFieldName } = useVehiclePickerContext()
  React.useEffect(() => {
    if (name) {
      registerFieldName(UseVehicleFields.IsUnknown, name)
    }
  }, [name, registerFieldName])

  return (
    <Form.Item {...formItemProps}>
      <RadioButtonCard
        onClick={toggleIsUnknown}
        initialSelected={isUnknown}
        title={<FormattedMessage id="label.select.car.dontknow" />}
      />
    </Form.Item>
  )
}

VehiclePicker.Year = VehicleYear
VehicleYear.id = 'vehicle-picker-year'
VehiclePicker.Year.id = VehicleYear.id

VehiclePicker.Make = VehicleMake
VehicleMake.id = 'vehicle-picker-make'
VehiclePicker.Make.id = VehicleMake.id

VehiclePicker.Model = VehicleModel
VehicleModel.id = 'vehicle-picker-model'
VehiclePicker.Model.id = VehicleModel.id

VehiclePicker.EngineType = VehicleFuelType
VehicleFuelType.id = 'vehicle-picker-engine-type'
VehiclePicker.EngineType.id = VehicleFuelType.id
VehiclePicker.GenericVehicleOption = GenericVehicleOption
